import { useState, useEffect } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { useAuth, useRequireAuth } from '../../utils/auth'
import { tenantApi } from '../../utils/api'
import { useToast } from '../../components/Toast'
import {
  LayoutDashboard,
  Tent,
  DollarSign,
  ClipboardList,
  Settings,
  LogOut,
  Plus,
  Calendar,
  Users,
  Link as LinkIcon,
  Archive,
  RotateCcw,
  Edit,
  ChevronRight,
  CreditCard,
  Mail,
  Phone,
  AlertTriangle,
  CheckCircle,
  Clock,
  XCircle,
  Search,
  CircleDot,
  Pause,
  Lock,
  Percent,
  Tag,
  FileText,
  User,
  Heart,
  Shield,
  Check,
  X,
} from 'lucide-react'

interface Camp {
  id: number
  name: string
  description: string
  startDate: string
  endDate: string
  ageMin: number
  ageMax: number
  maxSpots: number
  spotsTaken: number
  status: string
  registrationStatus: string
  waitlistEnabled: boolean
  waitlistMessage: string
  siblingDiscountEnabled: boolean
  siblingDiscountAmount: number
  siblingDiscountType: string
}

interface PricingItem {
  id: number
  campId: number | null
  name: string
  description: string
  amount: number
  itemType: string
  isRequired: boolean
  isActive: boolean
  displayOrder: number
}

interface Registration {
  id: number
  camp_id: number
  child_full_name: string
  child_dob: string
  parent_full_name: string
  email: string
  phone: string
  address: string
  emergency1_name: string
  emergency1_phone: string
  emergency1_relationship: string
  emergency2_name: string
  emergency2_phone: string
  emergency2_relationship: string
  authorised_collectors: string
  walk_home_alone: string
  has_medical_conditions: string
  medical_conditions_details: string
  has_additional_needs: string
  additional_needs_details: string
  has_allergies: string
  allergies_details: string
  has_medication: string
  medication_details: string
  has_further_info: string
  further_info_details: string
  permission_photos: number
  permission_health: number
  permission_activities: number
  permission_locations: number
  permission_meals: number
  permission_bathroom: number
  permission_first_aid: number
  permission_equipment: number
  permission_app_waiver: number
  payment_status: string
  payment_reference: string
  total_amount: number
  registration_status: string
  admin_notes: string
  created_at: string
}

type TabType = 'dashboard' | 'camps' | 'pricing' | 'registrations'

const VALID_TABS: TabType[] = ['dashboard', 'camps', 'pricing', 'registrations']

const TAB_CONFIG = {
  dashboard: { icon: LayoutDashboard, label: 'Dashboard' },
  camps: { icon: Tent, label: 'Camps' },
  pricing: { icon: DollarSign, label: 'Pricing' },
  registrations: { icon: ClipboardList, label: 'Registrations' },
}

export default function TenantAdmin() {
  const { slug } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const { tenant, login, logout } = useAuth()
  const { session, loading, authorized } = useRequireAuth('tenant')
  const { showToast, showConfirm } = useToast()
  
  const tabParam = searchParams.get('tab') as TabType | null
  const activeTab: TabType = tabParam && VALID_TABS.includes(tabParam) ? tabParam : 'dashboard'
  const selectedCampId = searchParams.get('campId') ? parseInt(searchParams.get('campId')!) : null
  
  const setActiveTab = (tab: TabType) => {
    const newParams = new URLSearchParams(searchParams)
    newParams.set('tab', tab)
    if (tab !== 'registrations') {
      newParams.delete('campId')
    }
    setSearchParams(newParams)
  }
  
  const setSelectedCampId = (campId: number | null) => {
    const newParams = new URLSearchParams(searchParams)
    if (campId !== null) {
      newParams.set('campId', campId.toString())
      newParams.set('tab', 'registrations')
    } else {
      newParams.delete('campId')
    }
    setSearchParams(newParams)
  }

  // Data state
  const [camps, setCamps] = useState<Camp[]>([])
  const [pricingItems, setPricingItems] = useState<PricingItem[]>([])
  const [registrations, setRegistrations] = useState<Registration[]>([])
  const [loadingData, setLoadingData] = useState(false)
  
  // Login form state
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loginError, setLoginError] = useState('')
  const [loggingIn, setLoggingIn] = useState(false)

  const basePath = slug ? `/t/${slug}` : ''

  useEffect(() => {
    if (authorized) {
      loadCamps()
      loadPricing()
    }
  }, [authorized])

  useEffect(() => {
    if (authorized && activeTab === 'registrations') {
      loadRegistrations()
    }
  }, [authorized, activeTab, selectedCampId])

  const loadCamps = async () => {
    try {
      const data = await tenantApi.getCamps(true)
      setCamps(data.camps || [])
    } catch (err) {
      console.error('Failed to load camps:', err)
    }
  }

  const loadPricing = async () => {
    try {
      const data = await tenantApi.getPricing()
      setPricingItems(data.items || [])
    } catch (err) {
      console.error('Failed to load pricing:', err)
    }
  }

  const loadRegistrations = async () => {
    setLoadingData(true)
    try {
      const data = await tenantApi.getRegistrations(selectedCampId || undefined)
      setRegistrations(data.registrations || [])
    } catch (err) {
      console.error('Failed to load registrations:', err)
    } finally {
      setLoadingData(false)
    }
  }

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoginError('')
    setLoggingIn(true)
    
    try {
      await login('tenant', email, password)
    } catch (err) {
      setLoginError(err instanceof Error ? err.message : 'Login failed')
    } finally {
      setLoggingIn(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-t-transparent" style={{ borderColor: tenant?.primaryColor || '#0ea5e9' }} />
      </div>
    )
  }

  // Login form
  if (!authorized) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-950 dark:to-gray-900 flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            {tenant?.logoUrl ? (
              <img src={tenant.logoUrl} alt={tenant.name} className="h-14 mx-auto mb-4" />
            ) : tenant && (
              <div 
                className="w-14 h-14 rounded-2xl mx-auto mb-4 flex items-center justify-center text-white font-bold text-xl shadow-lg"
                style={{ backgroundColor: tenant.primaryColor }}
              >
                {tenant.logoText || tenant.name.charAt(0)}
              </div>
            )}
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Welcome back</h1>
            <p className="text-gray-500 dark:text-gray-400 mt-2">Sign in to {tenant?.name || 'your account'}</p>
          </div>
          
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 p-8">
            <form onSubmit={handleLogin} className="space-y-6">
              {loginError && (
                <div className="flex items-center gap-3 p-4 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                  <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
                  <p className="text-red-600 dark:text-red-400 text-sm">{loginError}</p>
                </div>
              )}
              
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Email address
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input"
                  placeholder="you@example.com"
                  required
                />
              </div>
              
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input"
                  placeholder="••••••••"
                  required
                />
              </div>
              
              <button
                type="submit"
                disabled={loggingIn}
                className="w-full py-3 px-4 rounded-xl font-medium text-white transition-all disabled:opacity-50 hover:opacity-90"
                style={{ backgroundColor: tenant?.primaryColor || '#0ea5e9' }}
              >
                {loggingIn ? 'Signing in...' : 'Sign in'}
              </button>
            </form>
          </div>
          
          <p className="text-center text-gray-500 dark:text-gray-400 text-sm mt-6">
            <Link to={basePath || '/'} className="hover:underline inline-flex items-center gap-1">
              <ChevronRight className="w-4 h-4 rotate-180" />
              Back to registration
            </Link>
          </p>
        </div>
      </div>
    )
  }

  const currency = tenant?.currency === 'usd' ? '$' : 'AED'

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      {/* Header */}
      <header className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {tenant?.logoUrl ? (
              <img src={tenant.logoUrl} alt={tenant.name} className="h-9" />
            ) : tenant && (
              <div 
                className="w-9 h-9 rounded-xl flex items-center justify-center text-white font-bold shadow-sm"
                style={{ backgroundColor: tenant.primaryColor }}
              >
                {tenant.logoText || tenant.name.charAt(0)}
              </div>
            )}
            <div>
              <h1 className="font-semibold text-gray-900 dark:text-white">{tenant?.name}</h1>
              <p className="text-xs text-gray-500 dark:text-gray-400">{session?.email}</p>
            </div>
          </div>
          
          <nav className="flex items-center gap-2">
            <Link 
              to={`${basePath}/admin/settings`} 
              className="p-2 rounded-lg text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <Settings className="w-5 h-5" />
            </Link>
            <button 
              onClick={logout} 
              className="p-2 rounded-lg text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </nav>
        </div>
      </header>
      
      {/* Tabs */}
      <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-6">
          <nav className="flex gap-1 overflow-x-auto py-2">
            {VALID_TABS.map((tab) => {
              const { icon: Icon, label } = TAB_CONFIG[tab]
              const isActive = activeTab === tab
              return (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                    isActive
                      ? 'text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                  style={isActive ? { backgroundColor: tenant?.primaryColor } : {}}
                >
                  <Icon className="w-4 h-4" />
                  {label}
                </button>
              )
            })}
          </nav>
        </div>
      </div>
      
      {/* Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {activeTab === 'dashboard' && (
          <DashboardTab 
            camps={camps} 
            registrations={registrations}
            tenant={tenant!}
            currency={currency}
          />
        )}
        
        {activeTab === 'camps' && (
          <CampsTab 
            camps={camps}
            pricingItems={pricingItems}
            tenant={tenant!}
            currency={currency}
            onRefresh={loadCamps}
            onViewRegistrations={(campId) => setSelectedCampId(campId)}
            showToast={showToast}
            showConfirm={showConfirm}
          />
        )}
        
        {activeTab === 'pricing' && (
          <PricingTab 
            camps={camps}
            pricingItems={pricingItems}
            tenant={tenant!}
            currency={currency}
            onRefresh={loadPricing}
            showToast={showToast}
            showConfirm={showConfirm}
          />
        )}
        
        {activeTab === 'registrations' && (
          <RegistrationsTab 
            camps={camps}
            registrations={registrations}
            loadingData={loadingData}
            selectedCampId={selectedCampId}
            tenant={tenant!}
            currency={currency}
            onCampSelect={setSelectedCampId}
            onRefresh={loadRegistrations}
            showToast={showToast}
            showConfirm={showConfirm}
          />
        )}
      </main>
    </div>
  )
}

// Connect Stripe Button Component
function ConnectStripeButton({ tenant }: { tenant: { primaryColor: string } }) {
  const [loading, setLoading] = useState(false)
  const { showToast } = useToast()
  
  const handleConnect = async () => {
    setLoading(true)
    try {
      const data = await tenantApi.startStripeOnboard(
        window.location.href,
        window.location.href
      )
      if (data.url) {
        window.location.href = data.url
      } else {
        showToast('Failed to get Stripe onboarding URL', 'error')
      }
    } catch (err) {
      console.error('Failed to start Stripe onboard:', err)
      showToast(
        err instanceof Error ? err.message : 'Failed to connect Stripe. Please try again.',
        'error'
      )
    } finally {
      setLoading(false)
    }
  }
  
  return (
    <button 
      onClick={handleConnect}
      disabled={loading}
      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium text-white transition-all hover:opacity-90 disabled:opacity-50"
      style={{ backgroundColor: tenant.primaryColor }}
    >
      <CreditCard className="w-4 h-4" />
      {loading ? 'Connecting...' : 'Connect Stripe'}
    </button>
  )
}

// Dashboard Tab
function DashboardTab({ 
  camps, 
  registrations,
  tenant,
  currency
}: { 
  camps: Camp[]
  registrations: Registration[]
  tenant: { primaryColor: string; stripeOnboarded?: boolean }
  currency: string
}) {
  const activeCamps = camps.filter(c => c.status === 'active' && c.registrationStatus === 'open')
  const totalSpots = camps.reduce((sum, c) => sum + c.maxSpots, 0)
  const spotsTaken = camps.reduce((sum, c) => sum + c.spotsTaken, 0)
  const revenue = registrations.reduce((sum, r) => sum + (r.total_amount || 0), 0)

  const stats = [
    { label: 'Active Camps', value: activeCamps.length, icon: Tent, color: 'bg-emerald-500' },
    { label: 'Total Registrations', value: spotsTaken, icon: ClipboardList, color: 'bg-blue-500' },
    { label: 'Spots Available', value: totalSpots - spotsTaken, icon: Users, color: 'bg-violet-500' },
    { label: 'Revenue', value: `${currency} ${revenue.toFixed(0)}`, icon: DollarSign, color: 'bg-amber-500' },
  ]

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Dashboard</h2>
        <p className="text-gray-500 dark:text-gray-400 mt-1">Overview of your camp operations</p>
      </div>
      
      {/* Stats */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, i) => (
          <div key={i} className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <span className="text-gray-500 dark:text-gray-400 text-sm font-medium">{stat.label}</span>
              <div className={`${stat.color} w-10 h-10 rounded-xl flex items-center justify-center`}>
                <stat.icon className="w-5 h-5 text-white" />
              </div>
            </div>
            <p className="text-3xl font-bold text-gray-900 dark:text-white">{stat.value}</p>
          </div>
        ))}
      </div>
      
      {/* Stripe Setup */}
      {!tenant.stripeOnboarded && (
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-2xl border border-amber-200 dark:border-amber-800 p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-amber-100 dark:bg-amber-900/50 flex items-center justify-center flex-shrink-0">
              <CreditCard className="w-6 h-6 text-amber-600 dark:text-amber-400" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Set Up Payments</h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
                Connect your Stripe account to accept payments from registrations.
              </p>
              <ConnectStripeButton tenant={tenant} />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Camps Tab
function CampsTab({ 
  camps, 
  tenant, 
  currency,
  onRefresh,
  onViewRegistrations,
  showToast,
  showConfirm,
}: { 
  camps: Camp[]
  pricingItems: PricingItem[]
  tenant: { primaryColor: string }
  currency: string
  onRefresh: () => void
  onViewRegistrations: (campId: number) => void
  showToast: (msg: string, type?: 'success' | 'error' | 'warning' | 'info') => void
  showConfirm: (msg: string, onConfirm: () => void) => void
}) {
  const [showForm, setShowForm] = useState(false)
  const [editingCamp, setEditingCamp] = useState<Camp | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [showArchived, setShowArchived] = useState(false)

  const filteredCamps = camps.filter(camp => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      if (!camp.name.toLowerCase().includes(query) && !camp.description.toLowerCase().includes(query)) {
        return false
      }
    }
    if (!showArchived && camp.status === 'archived') return false
    return true
  })

  const handleArchiveCamp = (_id: number, currentStatus: string) => {
    const isArchived = currentStatus === 'archived'
    showConfirm(
      isArchived ? 'Restore this camp?' : 'Archive this camp?',
      async () => {
        showToast(isArchived ? 'Camp restored!' : 'Camp archived!', 'success')
        onRefresh()
      }
    )
  }

  if (showForm || editingCamp) {
    return (
      <CampForm
        camp={editingCamp}
        pricingItems={[]}
        tenant={tenant}
        currency={currency}
        onSubmit={async (data) => {
          try {
            if (editingCamp) {
              // TODO: Update camp API call
            } else {
              await tenantApi.createCamp(data)
            }
            showToast(editingCamp ? 'Camp updated!' : 'Camp created!', 'success')
            onRefresh()
            setShowForm(false)
            setEditingCamp(null)
          } catch (err) {
            showToast(err instanceof Error ? err.message : 'Failed to save camp', 'error')
          }
        }}
        onCancel={() => {
          setShowForm(false)
          setEditingCamp(null)
        }}
      />
    )
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Camps</h2>
          <p className="text-gray-500 dark:text-gray-400 mt-1">Manage your camp programs</p>
        </div>
        <button 
          onClick={() => setShowForm(true)} 
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium text-white transition-all hover:opacity-90"
          style={{ backgroundColor: tenant.primaryColor }}
        >
          <Plus className="w-4 h-4" />
          Create Camp
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search camps..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input pl-10"
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 cursor-pointer">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
            className="w-4 h-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
          />
          Show archived
        </label>
      </div>
      
      {filteredCamps.length === 0 ? (
        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 text-center py-16 px-4">
          <div className="w-16 h-16 rounded-2xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center mx-auto mb-4">
            <Tent className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No camps yet</h3>
          <p className="text-gray-500 dark:text-gray-400 mb-6 max-w-sm mx-auto">
            Create your first camp to start accepting registrations
          </p>
          <button 
            onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium text-white transition-all hover:opacity-90"
            style={{ backgroundColor: tenant.primaryColor }}
          >
            <Plus className="w-4 h-4" />
            Create Camp
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredCamps.map((camp) => {
            const today = new Date()
            today.setHours(0, 0, 0, 0)
            const endDate = new Date(camp.endDate)
            const hasEnded = endDate < today
            const spotsLeft = camp.maxSpots - camp.spotsTaken
            
            return (
              <div 
                key={camp.id} 
                className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-6 transition-opacity ${
                  camp.status === 'archived' || hasEnded ? 'opacity-60' : ''
                }`}
              >
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{camp.name}</h3>
                      {camp.status === 'archived' && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400">
                          <Archive className="w-3 h-3" />
                          Archived
                        </span>
                      )}
                      {hasEnded && camp.status !== 'archived' && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">
                          <Clock className="w-3 h-3" />
                          Ended
                        </span>
                      )}
                      {camp.status !== 'archived' && !hasEnded && (
                        camp.registrationStatus === 'paused' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">
                            <Pause className="w-3 h-3" />
                            Paused
                          </span>
                        ) : camp.registrationStatus === 'closed' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400">
                            <Lock className="w-3 h-3" />
                            Closed
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400">
                            <CircleDot className="w-3 h-3" />
                            Open
                          </span>
                        )
                      )}
                      {camp.waitlistEnabled && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">
                          <ClipboardList className="w-3 h-3" />
                          Waitlist
                        </span>
                      )}
                    </div>
                    <p className="text-gray-600 dark:text-gray-400 text-sm mb-3 line-clamp-2">{camp.description}</p>
                    <div className="flex flex-wrap gap-4 text-sm text-gray-500 dark:text-gray-400">
                      <span className="inline-flex items-center gap-1.5">
                        <Calendar className="w-4 h-4" />
                        {camp.startDate} - {camp.endDate}
                      </span>
                      <span className="inline-flex items-center gap-1.5">
                        <Users className="w-4 h-4" />
                        Ages {camp.ageMin}-{camp.ageMax}
                      </span>
                      <span className={`inline-flex items-center gap-1.5 ${spotsLeft <= 0 ? 'text-red-500' : 'text-emerald-500'}`}>
                        <CheckCircle className="w-4 h-4" />
                        {spotsLeft}/{camp.maxSpots} spots
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-2">
                    <button 
                      onClick={() => onViewRegistrations(camp.id)} 
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-white transition-all hover:opacity-90"
                      style={{ backgroundColor: tenant.primaryColor }}
                    >
                      <ClipboardList className="w-4 h-4" />
                      Registrations
                    </button>
                    {!hasEnded && camp.status !== 'archived' && (
                      <button 
                        onClick={() => setEditingCamp(camp)} 
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                      >
                        <Edit className="w-4 h-4" />
                        Edit
                      </button>
                    )}
                    <button
                      onClick={() => {
                        const url = `${window.location.origin}${window.location.pathname.replace('/admin', '')}/camp/${camp.id}`
                        navigator.clipboard.writeText(url)
                        showToast('Link copied!', 'success')
                      }}
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                    >
                      <LinkIcon className="w-4 h-4" />
                      Copy Link
                    </button>
                    <button
                      onClick={() => handleArchiveCamp(camp.id, camp.status)}
                      className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        camp.status === 'archived' 
                          ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-200 dark:hover:bg-emerald-900/50' 
                          : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/50'
                      }`}
                    >
                      {camp.status === 'archived' ? <RotateCcw className="w-4 h-4" /> : <Archive className="w-4 h-4" />}
                      {camp.status === 'archived' ? 'Restore' : 'Archive'}
                    </button>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// Camp Form Component
function CampForm({ 
  camp, 
  tenant,
  currency,
  onSubmit, 
  onCancel,
}: { 
  camp: Camp | null
  pricingItems?: PricingItem[]
  tenant: { primaryColor: string }
  currency: string
  onSubmit: (data: Partial<Camp>) => void
  onCancel: () => void
}) {
  const [formData, setFormData] = useState({
    name: camp?.name || '',
    description: camp?.description || '',
    startDate: camp?.startDate || '',
    endDate: camp?.endDate || '',
    ageMin: camp?.ageMin || 5,
    ageMax: camp?.ageMax || 15,
    maxSpots: camp?.maxSpots || 20,
    status: camp?.status || 'active',
    registrationStatus: camp?.registrationStatus || 'open',
    waitlistEnabled: camp?.waitlistEnabled || false,
    waitlistMessage: camp?.waitlistMessage || '',
    siblingDiscountEnabled: camp?.siblingDiscountEnabled || false,
    siblingDiscountAmount: camp?.siblingDiscountAmount || 0,
    siblingDiscountType: camp?.siblingDiscountType || 'fixed',
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit(formData)
  }

  return (
    <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-8 max-w-4xl">
      <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
        {camp ? 'Edit Camp' : 'Create New Camp'}
      </h2>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid md:grid-cols-2 gap-6">
          {/* Left Column */}
          <div className="space-y-4">
            <div>
              <label className="label">Camp Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="input"
                required
              />
            </div>
            
            <div>
              <label className="label">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="input resize-none"
                rows={3}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">Start Date *</label>
                <input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                  className="input"
                  required
                />
              </div>
              <div>
                <label className="label">End Date *</label>
                <input
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                  className="input"
                  required
                />
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="label">Min Age</label>
                <input
                  type="number"
                  value={formData.ageMin}
                  onChange={(e) => setFormData({ ...formData, ageMin: parseInt(e.target.value) })}
                  className="input"
                  min="3"
                  max="18"
                />
              </div>
              <div>
                <label className="label">Max Age</label>
                <input
                  type="number"
                  value={formData.ageMax}
                  onChange={(e) => setFormData({ ...formData, ageMax: parseInt(e.target.value) })}
                  className="input"
                  min="3"
                  max="18"
                />
              </div>
              <div>
                <label className="label">Max Spots</label>
                <input
                  type="number"
                  value={formData.maxSpots}
                  onChange={(e) => setFormData({ ...formData, maxSpots: parseInt(e.target.value) })}
                  className="input"
                  min="1"
                />
              </div>
            </div>
          </div>
          
          {/* Right Column */}
          <div className="space-y-4">
            <div>
              <label className="label">Registration Status</label>
              <div className="space-y-2">
                {[
                  { value: 'open', label: 'Open', icon: CircleDot, color: 'text-emerald-500' },
                  { value: 'paused', label: 'Paused', icon: Pause, color: 'text-amber-500' },
                  { value: 'closed', label: 'Closed', icon: Lock, color: 'text-red-500' },
                ].map((option) => (
                  <label 
                    key={option.value} 
                    className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                      formData.registrationStatus === option.value
                        ? 'border-gray-900 dark:border-white bg-gray-50 dark:bg-gray-800'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    <input
                      type="radio"
                      name="registrationStatus"
                      value={option.value}
                      checked={formData.registrationStatus === option.value}
                      onChange={(e) => setFormData({ ...formData, registrationStatus: e.target.value as any })}
                      className="sr-only"
                    />
                    <option.icon className={`w-5 h-5 ${option.color}`} />
                    <span className="text-gray-900 dark:text-white font-medium">{option.label}</span>
                  </label>
                ))}
              </div>
            </div>
            
            <div className="flex items-center gap-3 p-3 rounded-xl border border-gray-200 dark:border-gray-700">
              <input
                type="checkbox"
                id="waitlistEnabled"
                checked={formData.waitlistEnabled}
                onChange={(e) => setFormData({ ...formData, waitlistEnabled: e.target.checked })}
                className="w-4 h-4 rounded border-gray-300"
              />
              <label htmlFor="waitlistEnabled" className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                <ClipboardList className="w-4 h-4 text-gray-400" />
                Enable Waitlist
              </label>
            </div>
            
            {formData.waitlistEnabled && (
              <div>
                <label className="label">Waitlist Message</label>
                <input
                  type="text"
                  value={formData.waitlistMessage}
                  onChange={(e) => setFormData({ ...formData, waitlistMessage: e.target.value })}
                  className="input"
                  placeholder="We'll contact you if a spot opens up"
                />
              </div>
            )}
            
            <div className="flex items-center gap-3 p-3 rounded-xl border border-gray-200 dark:border-gray-700">
              <input
                type="checkbox"
                id="siblingDiscountEnabled"
                checked={formData.siblingDiscountEnabled}
                onChange={(e) => setFormData({ ...formData, siblingDiscountEnabled: e.target.checked })}
                className="w-4 h-4 rounded border-gray-300"
              />
              <label htmlFor="siblingDiscountEnabled" className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                <Percent className="w-4 h-4 text-gray-400" />
                Enable Sibling Discount
              </label>
            </div>
            
            {formData.siblingDiscountEnabled && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Discount Type</label>
                  <select
                    value={formData.siblingDiscountType}
                    onChange={(e) => setFormData({ ...formData, siblingDiscountType: e.target.value as any })}
                    className="input"
                  >
                    <option value="fixed">Fixed ({currency})</option>
                    <option value="percentage">Percentage (%)</option>
                  </select>
                </div>
                <div>
                  <label className="label">
                    {formData.siblingDiscountType === 'percentage' ? 'Discount %' : `Amount (${currency})`}
                  </label>
                  <input
                    type="number"
                    value={formData.siblingDiscountAmount}
                    onChange={(e) => setFormData({ ...formData, siblingDiscountAmount: parseFloat(e.target.value) || 0 })}
                    className="input"
                    min="0"
                    step={formData.siblingDiscountType === 'percentage' ? '1' : '0.01'}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
        
        <div className="flex gap-3 pt-6 border-t border-gray-200 dark:border-gray-700">
          <button 
            type="submit" 
            className="flex-1 py-3 px-4 rounded-xl font-medium text-white transition-all hover:opacity-90"
            style={{ backgroundColor: tenant.primaryColor }}
          >
            {camp ? 'Update Camp' : 'Create Camp'}
          </button>
          <button 
            type="button" 
            onClick={onCancel} 
            className="px-6 py-3 rounded-xl font-medium bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  )
}

// Pricing Tab
function PricingTab({ 
  camps,
  pricingItems,
  tenant, 
  currency,
  onRefresh,
  showToast,
}: { 
  camps: Camp[]
  pricingItems: PricingItem[]
  tenant: { primaryColor: string }
  currency: string
  onRefresh: () => void
  showToast: (msg: string, type?: 'success' | 'error' | 'warning' | 'info') => void
  showConfirm: (msg: string, onConfirm: () => void) => void
}) {
  const [showForm, setShowForm] = useState(false)
  const [editingItem, setEditingItem] = useState<PricingItem | null>(null)

  if (showForm || editingItem) {
    return (
      <PricingForm
        item={editingItem}
        camps={camps}
        tenant={tenant}
        currency={currency}
        onSubmit={async () => {
          try {
            showToast(editingItem ? 'Pricing item updated!' : 'Pricing item created!', 'success')
            onRefresh()
            setShowForm(false)
            setEditingItem(null)
          } catch (err) {
            showToast(err instanceof Error ? err.message : 'Failed to save', 'error')
          }
        }}
        onCancel={() => {
          setShowForm(false)
          setEditingItem(null)
        }}
      />
    )
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Pricing Items</h2>
          <p className="text-gray-500 dark:text-gray-400 mt-1">Manage fees, add-ons, and discounts</p>
        </div>
        <button 
          onClick={() => setShowForm(true)}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium text-white transition-all hover:opacity-90"
          style={{ backgroundColor: tenant.primaryColor }}
        >
          <Plus className="w-4 h-4" />
          Create Pricing Item
        </button>
      </div>
      
      {pricingItems.length === 0 ? (
        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 text-center py-16 px-4">
          <div className="w-16 h-16 rounded-2xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center mx-auto mb-4">
            <DollarSign className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No pricing items yet</h3>
          <p className="text-gray-500 dark:text-gray-400 mb-6 max-w-sm mx-auto">
            Create pricing items to assign to your camps
          </p>
          <button 
            onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium text-white transition-all hover:opacity-90"
            style={{ backgroundColor: tenant.primaryColor }}
          >
            <Plus className="w-4 h-4" />
            Create Pricing Item
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {pricingItems.map((item) => {
            const assignedCamp = item.campId ? camps.find(c => c.id === item.campId) : null
            
            return (
              <div 
                key={item.id} 
                className={`bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-4 transition-opacity ${
                  !item.isActive ? 'opacity-60' : ''
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-gray-900 dark:text-white">{item.name}</span>
                      {!item.isActive && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-500">
                          <Archive className="w-3 h-3" />
                          Archived
                        </span>
                      )}
                      {item.isRequired && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">
                          <Shield className="w-3 h-3" />
                          Required
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 mt-1">{item.description}</p>
                    <p className="text-xs text-gray-400 mt-1 flex items-center gap-2">
                      <Tag className="w-3 h-3" />
                      {item.itemType}
                      {assignedCamp && (
                        <>
                          <span className="text-gray-300">•</span>
                          <Tent className="w-3 h-3" />
                          {assignedCamp.name}
                        </>
                      )}
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className={`text-lg font-bold ${item.amount < 0 ? 'text-emerald-600' : 'text-gray-900 dark:text-white'}`}>
                      {item.amount < 0 ? '-' : ''}{currency} {Math.abs(item.amount).toFixed(2)}
                    </span>
                    <button 
                      onClick={() => setEditingItem(item)} 
                      className="p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// Pricing Form
function PricingForm({ 
  item, 
  camps,
  tenant,
  currency,
  onSubmit, 
  onCancel,
}: { 
  item: PricingItem | null
  camps: Camp[]
  tenant: { primaryColor: string }
  currency: string
  onSubmit: (data: Partial<PricingItem>) => void
  onCancel: () => void
}) {
  const [formData, setFormData] = useState({
    name: item?.name || '',
    description: item?.description || '',
    amount: item?.amount || 0,
    itemType: item?.itemType || 'add_on',
    campId: item?.campId || null as number | null,
    isRequired: item?.isRequired || false,
    isActive: item?.isActive ?? true,
    displayOrder: item?.displayOrder || 1,
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit(formData)
  }

  return (
    <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-8 max-w-2xl">
      <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
        {item ? 'Edit Pricing Item' : 'Create Pricing Item'}
      </h2>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="label">Item Name *</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="input"
            required
            placeholder="e.g., Camp Fee"
          />
        </div>
        
        <div>
          <label className="label">Description</label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            className="input resize-none"
            rows={2}
            placeholder="Optional description"
          />
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label">Amount ({currency}) *</label>
            <input
              type="number"
              step="0.01"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) })}
              className="input"
              required
            />
            <p className="text-xs text-gray-500 mt-1">Use negative for discounts</p>
          </div>
          <div>
            <label className="label">Type</label>
            <select
              value={formData.itemType}
              onChange={(e) => setFormData({ ...formData, itemType: e.target.value })}
              className="input"
            >
              <option value="base_fee">Base Fee</option>
              <option value="add_on">Add-on</option>
              <option value="discount">Discount</option>
            </select>
          </div>
        </div>
        
        <div>
          <label className="label">Assign to Camp</label>
          <select
            value={formData.campId || ''}
            onChange={(e) => setFormData({ ...formData, campId: e.target.value ? parseInt(e.target.value) : null })}
            className="input"
          >
            <option value="">All Camps</option>
            {camps.map(camp => (
              <option key={camp.id} value={camp.id}>{camp.name}</option>
            ))}
          </select>
        </div>
        
        <div className="flex items-center gap-6">
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.isRequired}
              onChange={(e) => setFormData({ ...formData, isRequired: e.target.checked })}
              className="w-4 h-4 rounded border-gray-300"
            />
            Required (auto-selected)
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              className="w-4 h-4 rounded border-gray-300"
            />
            Active
          </label>
        </div>
        
        <div className="flex gap-3 pt-6 border-t border-gray-200 dark:border-gray-700">
          <button 
            type="submit" 
            className="flex-1 py-3 px-4 rounded-xl font-medium text-white transition-all hover:opacity-90"
            style={{ backgroundColor: tenant.primaryColor }}
          >
            {item ? 'Update Item' : 'Create Item'}
          </button>
          <button 
            type="button" 
            onClick={onCancel} 
            className="px-6 py-3 rounded-xl font-medium bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  )
}

// Registrations Tab
function RegistrationsTab({ 
  camps,
  registrations,
  loadingData,
  selectedCampId,
  tenant,
  currency,
  onCampSelect,
  onRefresh,
  showToast,
  showConfirm,
}: { 
  camps: Camp[]
  registrations: Registration[]
  loadingData: boolean
  selectedCampId: number | null
  tenant: { primaryColor: string }
  currency: string
  onCampSelect: (campId: number | null) => void
  onRefresh: () => void
  showToast: (msg: string, type?: 'success' | 'error' | 'warning' | 'info') => void
  showConfirm: (msg: string, onConfirm: () => void) => void
}) {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedRegistration, setSelectedRegistration] = useState<Registration | null>(null)

  const filteredRegistrations = registrations.filter(reg => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      return (
        reg.child_full_name?.toLowerCase().includes(query) ||
        reg.parent_full_name?.toLowerCase().includes(query) ||
        reg.email?.toLowerCase().includes(query) ||
        reg.phone?.includes(query)
      )
    }
    return true
  })

  const stats = {
    total: filteredRegistrations.length,
    paid: filteredRegistrations.filter(r => r.payment_status === 'paid' || r.payment_status === 'free').length,
    pending: filteredRegistrations.filter(r => !r.payment_status || r.payment_status === 'pending').length,
    revenue: filteredRegistrations.reduce((sum, r) => sum + (r.total_amount || 0), 0),
  }

  const getCampName = (campId: number) => camps.find(c => c.id === campId)?.name || 'Unknown'

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('en-GB', { 
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' 
    })
  }

  if (selectedRegistration) {
    return (
      <RegistrationDetail
        registration={selectedRegistration}
        camps={camps}
        tenant={tenant}
        currency={currency}
        onBack={() => setSelectedRegistration(null)}
        onUpdate={(updated) => {
          setSelectedRegistration(updated)
          onRefresh()
        }}
        showToast={showToast}
        showConfirm={showConfirm}
      />
    )
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Registrations</h2>
          <p className="text-gray-500 dark:text-gray-400 mt-1">View and manage registrations</p>
        </div>
        <select
          value={selectedCampId || ''}
          onChange={(e) => onCampSelect(e.target.value ? parseInt(e.target.value) : null)}
          className="input w-auto"
        >
          <option value="">All Camps</option>
          {camps.map((camp) => (
            <option key={camp.id} value={camp.id}>{camp.name}</option>
          ))}
        </select>
      </div>

      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          placeholder="Search by name, email, phone..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="input pl-10"
        />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Total', value: stats.total, icon: ClipboardList },
          { label: 'Paid/Free', value: stats.paid, icon: CheckCircle, color: 'text-emerald-600' },
          { label: 'Pending', value: stats.pending, icon: Clock, color: 'text-amber-600' },
          { label: 'Revenue', value: `${currency} ${stats.revenue.toFixed(0)}`, icon: DollarSign },
        ].map((stat, i) => (
          <div key={i} className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-4 text-center">
            <div className={`text-2xl font-bold ${stat.color || 'text-gray-900 dark:text-white'}`}>{stat.value}</div>
            <div className="text-xs text-gray-500 uppercase mt-1 flex items-center justify-center gap-1">
              <stat.icon className="w-3 h-3" />
              {stat.label}
            </div>
          </div>
        ))}
      </div>
      
      {loadingData ? (
        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 text-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-t-transparent mx-auto" style={{ borderColor: tenant.primaryColor }} />
        </div>
      ) : filteredRegistrations.length === 0 ? (
        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 text-center py-16 px-4">
          <div className="w-16 h-16 rounded-2xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center mx-auto mb-4">
            <ClipboardList className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No registrations</h3>
          <p className="text-gray-500 dark:text-gray-400">
            {searchQuery ? 'No registrations match your search' : 'Registrations will appear here once families sign up'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredRegistrations.map((reg) => (
            <div 
              key={reg.id}
              onClick={() => setSelectedRegistration(reg)}
              className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-4 cursor-pointer hover:border-gray-300 dark:hover:border-gray-700 transition-all group"
            >
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="font-medium text-gray-900 dark:text-white">{reg.child_full_name || 'Unknown'}</span>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium ${
                      reg.registration_status === 'confirmed' ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400' :
                      reg.registration_status === 'waitlist' ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400' :
                      reg.registration_status === 'cancelled' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400' :
                      'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                    }`}>
                      {reg.registration_status === 'confirmed' && <CheckCircle className="w-3 h-3" />}
                      {reg.registration_status === 'waitlist' && <Clock className="w-3 h-3" />}
                      {reg.registration_status === 'cancelled' && <XCircle className="w-3 h-3" />}
                      {reg.registration_status || 'confirmed'}
                    </span>
                    {reg.payment_status !== 'free' && (
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium ${
                        reg.payment_status === 'paid' ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400' :
                        'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
                      }`}>
                        <CreditCard className="w-3 h-3" />
                        {reg.payment_status || 'pending'}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 flex items-center gap-2">
                    <User className="w-3.5 h-3.5" />
                    {reg.parent_full_name}
                    <span className="text-gray-300">•</span>
                    <Mail className="w-3.5 h-3.5" />
                    {reg.email}
                  </p>
                  <p className="text-xs text-gray-400 mt-1 flex items-center gap-2">
                    <Tent className="w-3 h-3" />
                    {getCampName(reg.camp_id)}
                    <span className="text-gray-300">•</span>
                    <Calendar className="w-3 h-3" />
                    {formatDate(reg.created_at)}
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <div className="text-lg font-bold text-gray-900 dark:text-white">
                      {currency} {(reg.total_amount || 0).toFixed(2)}
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-gray-500 transition-colors" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// Registration Detail Component
function RegistrationDetail({ 
  registration, 
  camps, 
  tenant,
  currency,
  onBack, 
}: { 
  registration: Registration
  camps: Camp[]
  tenant: { primaryColor: string }
  currency: string
  onBack: () => void
  onUpdate: (updated: Registration) => void
  showToast: (msg: string, type?: 'success' | 'error' | 'warning' | 'info') => void
  showConfirm: (msg: string, onConfirm: () => void) => void
}) {
  const getCampName = (campId: number) => camps.find(c => c.id === campId)?.name || 'Unknown'

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('en-GB', { 
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' 
    })
  }

  return (
    <div>
      <button 
        onClick={onBack} 
        className="inline-flex items-center gap-2 text-sm font-medium mb-6 hover:underline"
        style={{ color: tenant.primaryColor }}
      >
        <ChevronRight className="w-4 h-4 rotate-180" />
        Back to Registrations
      </button>

      <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{registration.child_full_name || 'Unknown'}</h2>
              <p className="text-gray-500 flex items-center gap-2 mt-1">
                <Tent className="w-4 h-4" />
                {getCampName(registration.camp_id)}
              </p>
              <p className="text-gray-400 text-xs mt-1 flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                Registered: {formatDate(registration.created_at)}
              </p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold" style={{ color: tenant.primaryColor }}>
                {currency} {(registration.total_amount || 0).toFixed(2)}
              </div>
              <div className="flex gap-2 mt-2 justify-end">
                <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium ${
                  registration.registration_status === 'confirmed' ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400' :
                  registration.registration_status === 'waitlist' ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400' :
                  registration.registration_status === 'cancelled' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400' :
                  'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                }`}>
                  {registration.registration_status === 'confirmed' && <CheckCircle className="w-3 h-3" />}
                  {registration.registration_status || 'confirmed'}
                </span>
                <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium ${
                  registration.payment_status === 'paid' ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400' :
                  registration.payment_status === 'free' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400' :
                  'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
                }`}>
                  <CreditCard className="w-3 h-3" />
                  {registration.payment_status || 'pending'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Contact */}
        <div className="px-6 py-4 bg-gray-50 dark:bg-gray-800/50 flex flex-wrap gap-2">
          {registration.email && (
            <a 
              href={`mailto:${registration.email}`} 
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <Mail className="w-4 h-4" />
              Email
            </a>
          )}
          {registration.phone && (
            <a 
              href={`tel:${registration.phone}`} 
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <Phone className="w-4 h-4" />
              Call
            </a>
          )}
          {registration.emergency1_phone && (
            <a 
              href={`tel:${registration.emergency1_phone}`} 
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
            >
              <AlertTriangle className="w-4 h-4" />
              Emergency 1
            </a>
          )}
        </div>

        <div className="p-6">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Child Info */}
            <div>
              <h3 className="text-sm font-semibold uppercase tracking-wider mb-4 flex items-center gap-2" style={{ color: tenant.primaryColor }}>
                <User className="w-4 h-4" />
                Child Information
              </h3>
              <dl className="space-y-3 text-sm">
                <div className="flex justify-between"><dt className="text-gray-500">Name</dt><dd className="text-gray-900 dark:text-white font-medium">{registration.child_full_name}</dd></div>
                <div className="flex justify-between"><dt className="text-gray-500">Date of Birth</dt><dd className="text-gray-900 dark:text-white">{registration.child_dob}</dd></div>
                <div className="flex justify-between"><dt className="text-gray-500">Walk Home Alone</dt><dd className="text-gray-900 dark:text-white">{registration.walk_home_alone}</dd></div>
              </dl>
            </div>

            {/* Parent Info */}
            <div>
              <h3 className="text-sm font-semibold uppercase tracking-wider mb-4 flex items-center gap-2" style={{ color: tenant.primaryColor }}>
                <Users className="w-4 h-4" />
                Parent/Guardian
              </h3>
              <dl className="space-y-3 text-sm">
                <div className="flex justify-between"><dt className="text-gray-500">Name</dt><dd className="text-gray-900 dark:text-white font-medium">{registration.parent_full_name}</dd></div>
                <div className="flex justify-between"><dt className="text-gray-500">Email</dt><dd><a href={`mailto:${registration.email}`} className="hover:underline" style={{ color: tenant.primaryColor }}>{registration.email}</a></dd></div>
                <div className="flex justify-between"><dt className="text-gray-500">Phone</dt><dd><a href={`tel:${registration.phone}`} className="hover:underline" style={{ color: tenant.primaryColor }}>{registration.phone}</a></dd></div>
                <div className="flex justify-between"><dt className="text-gray-500">Address</dt><dd className="text-gray-900 dark:text-white text-right">{registration.address || '-'}</dd></div>
              </dl>
            </div>

            {/* Emergency Contact 1 */}
            <div>
              <h3 className="text-sm font-semibold uppercase tracking-wider mb-4 flex items-center gap-2" style={{ color: tenant.primaryColor }}>
                <AlertTriangle className="w-4 h-4" />
                Emergency Contact 1
              </h3>
              <dl className="space-y-3 text-sm">
                <div className="flex justify-between"><dt className="text-gray-500">Name</dt><dd className="text-gray-900 dark:text-white font-medium">{registration.emergency1_name}</dd></div>
                <div className="flex justify-between"><dt className="text-gray-500">Phone</dt><dd><a href={`tel:${registration.emergency1_phone}`} className="hover:underline" style={{ color: tenant.primaryColor }}>{registration.emergency1_phone}</a></dd></div>
                <div className="flex justify-between"><dt className="text-gray-500">Relationship</dt><dd className="text-gray-900 dark:text-white">{registration.emergency1_relationship}</dd></div>
              </dl>
            </div>

            {/* Emergency Contact 2 */}
            {registration.emergency2_name && (
              <div>
                <h3 className="text-sm font-semibold uppercase tracking-wider mb-4 flex items-center gap-2" style={{ color: tenant.primaryColor }}>
                  <AlertTriangle className="w-4 h-4" />
                  Emergency Contact 2
                </h3>
                <dl className="space-y-3 text-sm">
                  <div className="flex justify-between"><dt className="text-gray-500">Name</dt><dd className="text-gray-900 dark:text-white font-medium">{registration.emergency2_name}</dd></div>
                  <div className="flex justify-between"><dt className="text-gray-500">Phone</dt><dd><a href={`tel:${registration.emergency2_phone}`} className="hover:underline" style={{ color: tenant.primaryColor }}>{registration.emergency2_phone}</a></dd></div>
                  <div className="flex justify-between"><dt className="text-gray-500">Relationship</dt><dd className="text-gray-900 dark:text-white">{registration.emergency2_relationship}</dd></div>
                </dl>
              </div>
            )}
          </div>

          {/* Medical Info */}
          {(registration.has_medical_conditions === 'yes' || registration.has_allergies === 'yes' || registration.has_additional_needs === 'yes' || registration.has_medication === 'yes') && (
            <div className="mt-8 pt-8 border-t border-gray-200 dark:border-gray-700">
              <h3 className="text-sm font-semibold uppercase tracking-wider mb-4 flex items-center gap-2" style={{ color: tenant.primaryColor }}>
                <Heart className="w-4 h-4" />
                Medical Information
              </h3>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                {registration.has_medical_conditions === 'yes' && (
                  <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-xl">
                    <dt className="text-gray-500 text-xs uppercase tracking-wider mb-2">Medical Conditions</dt>
                    <dd className="text-gray-900 dark:text-white">{registration.medical_conditions_details}</dd>
                  </div>
                )}
                {registration.has_allergies === 'yes' && (
                  <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-xl">
                    <dt className="text-gray-500 text-xs uppercase tracking-wider mb-2">Allergies</dt>
                    <dd className="text-gray-900 dark:text-white">{registration.allergies_details}</dd>
                  </div>
                )}
                {registration.has_additional_needs === 'yes' && (
                  <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-xl">
                    <dt className="text-gray-500 text-xs uppercase tracking-wider mb-2">Additional Needs</dt>
                    <dd className="text-gray-900 dark:text-white">{registration.additional_needs_details}</dd>
                  </div>
                )}
                {registration.has_medication === 'yes' && (
                  <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-xl">
                    <dt className="text-gray-500 text-xs uppercase tracking-wider mb-2">Medication</dt>
                    <dd className="text-gray-900 dark:text-white">{registration.medication_details}</dd>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Authorised Collectors */}
          {registration.authorised_collectors && (
            <div className="mt-8 pt-8 border-t border-gray-200 dark:border-gray-700">
              <h3 className="text-sm font-semibold uppercase tracking-wider mb-4 flex items-center gap-2" style={{ color: tenant.primaryColor }}>
                <Shield className="w-4 h-4" />
                Authorised Collectors
              </h3>
              <p className="text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-800 p-4 rounded-xl whitespace-pre-line">
                {registration.authorised_collectors}
              </p>
            </div>
          )}

          {/* Permissions */}
          <div className="mt-8 pt-8 border-t border-gray-200 dark:border-gray-700">
            <h3 className="text-sm font-semibold uppercase tracking-wider mb-4 flex items-center gap-2" style={{ color: tenant.primaryColor }}>
              <FileText className="w-4 h-4" />
              Permissions
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
              {[
                { label: 'Photos/Videos', value: registration.permission_photos },
                { label: 'Medical Treatment', value: registration.permission_health },
                { label: 'Activities', value: registration.permission_activities },
                { label: 'Off-site Visits', value: registration.permission_locations },
                { label: 'Meals/Snacks', value: registration.permission_meals },
                { label: 'Bathroom', value: registration.permission_bathroom },
                { label: 'First Aid', value: registration.permission_first_aid },
                { label: 'Equipment', value: registration.permission_equipment },
                { label: 'Terms & Conditions', value: registration.permission_app_waiver },
              ].map(({ label, value }) => (
                <div key={label} className="flex items-center gap-2">
                  <span className={`w-6 h-6 rounded-lg flex items-center justify-center ${
                    value ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600' : 'bg-red-100 dark:bg-red-900/30 text-red-600'
                  }`}>
                    {value ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                  </span>
                  <span className="text-gray-700 dark:text-gray-300">{label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Meta */}
          <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700 flex flex-wrap justify-between gap-4 text-xs text-gray-400">
            <span className="flex items-center gap-1"><FileText className="w-3 h-3" /> ID: {registration.id}</span>
            {registration.payment_reference && <span className="flex items-center gap-1"><CreditCard className="w-3 h-3" /> Ref: {registration.payment_reference}</span>}
            <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> Created: {formatDate(registration.created_at)}</span>
          </div>
        </div>
      </div>
    </div>
  )
}
